import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, ClipboardList } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useUser } from '@/shared/hooks/useAuth';
import { useTasks, useTaskMutations, useProjectOptions } from '../hooks/useTasks';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { TasksFilterBar } from '../components/TasksFilterBar';
import { TasksKanban } from '../components/TasksKanban';
import { TaskFormModal } from '../components/TaskFormModal';
import { TaskDrawer } from '../components/TaskDrawer';
import type { Task, TaskStatus, TasksFilters, CreateTaskPayload, UpdateTaskPayload } from '../types';

export function TasksPage() {
  const user = useUser();
  const isAdmin = ['admin', 'superadmin'].includes(user?.role ?? '');

  // ─── Filters ──────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [projectId, setProjectId] = useState('');
  const [assignedTo, setAssignedTo] = useState('');

  const filters: TasksFilters = {
    ...(projectId ? { projectId } : {}),
    ...(assignedTo ? { assignedTo } : {}),
  };

  // ─── Data ─────────────────────────────────────────────────────────────────
  const { data: tasks = [], isLoading, isError } = useTasks(filters);
  const { data: projects = [] } = useProjectOptions();
  const { data: users = [] } = useUserOptions();
  const { createTask, updateTask, deleteTask } = useTaskMutations();

  // ─── UI State ─────────────────────────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false);
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [drawerTask, setDrawerTask] = useState<Task | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleCreate = () => {
    setEditTask(null);
    setFormOpen(true);
  };

  const handleEdit = useCallback((task: Task) => {
    setEditTask(task);
    setFormOpen(true);
  }, []);

  const handleOpenDrawer = useCallback((task: Task) => {
    setDrawerTask(task);
  }, []);

  const handleFormSubmit = async (payload: CreateTaskPayload | UpdateTaskPayload) => {
    if (editTask) {
      // Update
      updateTask.mutate(
        { id: editTask.id, payload: payload as UpdateTaskPayload },
        {
          onSuccess: () => {
            toast.success('Task updated.');
            setFormOpen(false);
            setEditTask(null);
          },
          onError: () => toast.error('Failed to update task.'),
        },
      );
    } else {
      // Create
      createTask.mutate(payload as CreateTaskPayload, {
        onSuccess: () => {
          toast.success('Task created.');
          setFormOpen(false);
        },
        onError: () => toast.error('Failed to create task.'),
      });
    }
  };

  const handleDrawerSave = (id: string, payload: UpdateTaskPayload) => {
    updateTask.mutate(
      { id, payload },
      {
        onSuccess: () => {
          toast.success('Task updated.');
          setDrawerTask(null);
        },
        onError: () => toast.error('Failed to update task.'),
      },
    );
  };

  const handleQuickStatus = (id: string, status: TaskStatus) => {
    updateTask.mutate(
      { id, payload: { status } },
      {
        onSuccess: () => toast.success(`Task → ${status.replace('_', ' ')}`, { duration: 2000 }),
        onError: () => toast.error('Failed to update status.'),
      },
    );
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    deleteTask.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Task deleted.');
        setDeleteTarget(null);
      },
      onError: () => toast.error('Failed to delete task.'),
    });
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full flex-col gap-4 p-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-display-sm font-semibold text-on-surface">Task Management</h1>
            <p className="text-body-sm text-on-surface-variant">
              {tasks.length} task{tasks.length !== 1 ? 's' : ''} total
            </p>
          </div>
        </div>
        <Button onClick={handleCreate} size="md">
          <Plus className="h-4 w-4" />
          New Task
        </Button>
      </div>

      {/* Filter Bar */}
      <TasksFilterBar
        search={search}
        projectId={projectId}
        assignedTo={assignedTo}
        isAdmin={isAdmin}
        projects={projects}
        users={users}
        onSearchChange={setSearch}
        onProjectChange={setProjectId}
        onAssignedToChange={setAssignedTo}
      />

      {/* Board */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <EmptyState
          title="Failed to load tasks"
          description="Check your connection or try refreshing."
        />
      ) : tasks.length === 0 && !search ? (
        <EmptyState
          title="No tasks yet"
          description="Create your first task to get started."
          action={<Button onClick={handleCreate}>New Task</Button>}
        />
      ) : (
        <TasksKanban
          tasks={tasks}
          search={search}
          onEdit={handleEdit}
          onDelete={setDeleteTarget}
          onOpenDrawer={handleOpenDrawer}
          onStatusChange={handleQuickStatus}
        />
      )}

      {/* Create / Edit Modal */}
      <TaskFormModal
        open={formOpen}
        task={editTask}
        projects={projects}
        users={users}
        isAdmin={isAdmin}
        loading={createTask.isPending || updateTask.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditTask(null);
        }}
        onSubmit={handleFormSubmit}
      />

      {/* Detail Drawer */}
      <TaskDrawer
        task={drawerTask}
        projects={projects}
        users={users}
        isAdmin={isAdmin}
        saving={updateTask.isPending}
        onClose={() => setDrawerTask(null)}
        onSave={handleDrawerSave}
      />

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Task"
        message={`Are you sure you want to delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteTask.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
